import React, {createContext, useCallback, useContext, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {
  SemanticLegendsQueryDocument,
  useCreateSemanticLegendMutation,
  useUpdateSemanticLegendMutation,
} from '../../../../generated/graphql';
import {combinedFilter} from '../../../../state/graphql/runSelectorQuery';
import {useSelector} from '../../../../state/hooks';
import {usePart, useViewAction} from '../../../../state/views/hooks';
import {Ref as RunSetRef} from '../../../../state/views/runSet/types';
import * as WorkspaceSettingsActions from '../../../../state/views/workspaceSettings/actions';
import {EMPTY_FILTERS} from '../../../../util/filters';
import {logCustomActionToDatadog} from '../../../../util/profiler/report';
import {useRampFlagSemanticLegendsCategoricalConfig} from '../../../../util/rampFeatureFlags';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useIsSaveWorkspaceAllowed} from '../../../WorkspaceBanners/useWorkspaceBanners';
import {ClearSettingFnType} from '../hooks/useSettingOverrides';
import {RunColorOptions, SemanticLegendSettings} from '../types';
import {useWorkspaceSettingsSemanticLegendPalette} from '../WorkspaceSettingsContextProvider';
import {SettingType} from './../../../../services/analytics/workspaceSettingsEvents';
import {useRunSetProject} from './../../../../state/views/runSet/hooks';
import {SettingOverrideCounts} from './../../../../state/views/workspaceSettings/settingOverrideUtils';
import {Ref as WorkspaceSettingsRef} from './../../../../state/views/workspaceSettings/types';
import {useOptimisticUpdate} from './../../../../util/hooks/useOptimisticUpdate';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';
import {
  SemanticLegendSettingsWithCategoricalConfigContext,
  SemanticLegendSettingsWithCategoricalConfigContextType,
} from './SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {
  useGrouping,
  useGroupingToastNotification,
} from './useGroupingToastNotification';
import {getMutationVarsFromSettings} from './utils/getMutationVarsFromSettings';
import {recalculateSemanticLegendBuckets} from './utils/recalculateSemanticLegendBuckets';
import {useSemanticLegendSettingsQueryProps} from './utils/useSemanticLegendSettingsQueryProps';
import {useWorkspaceConfigKeys} from './utils/useWorkspaceConfigKeys';

export function isDefaultSemanticLegendSettings(
  semanticLegendSettings: SemanticLegendSettings
) {
  return (
    !semanticLegendSettings ||
    !semanticLegendSettings.key ||
    semanticLegendSettings.key === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key
  );
}

export type SemanticLegendSettingsContextType = {
  type: 'without_categorical_config';
  clearSetting: ClearSettingFnType;
  configKeys: string[];
  hasActiveGrouping: boolean;
  // Whether the semantic legend is metric based colors
  isMetricBasedColors: boolean;
  // Whether the semantic legend spec has previously been created
  specPreviouslyCreated: boolean;
  // The semantic legend run color options - metric based colors or original project colors
  semanticLegendRunColorOptions?: RunColorOptions;
  // The semantic legend settings
  semanticLegendSettings?: SemanticLegendSettings;
  /**
   * These are the settings for the specified settingLevel. This should match what's in the view spec, but not necessarily
   * be the value that is displayed depending on the settingLevel
   */
  settingLevel: 'workspace';
  settingOverrideCounts: SettingOverrideCounts;
  trackSemanticLegendSettings: (
    setting: SettingType,
    value: string | undefined
  ) => void;
  updateSemanticLegendRunColorOptions?: (
    runColorOptions: RunColorOptions
  ) => void;
  /**
   * Updates the semantic legend settings
   */
  updateSemanticLegendSettings?: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  /**
   * Updates the semantic legend settings optimistically
   */
  updateSemanticLegendSettingsOptimistically?: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
};

export const SemanticLegendSettingsContext = createContext<
  SemanticLegendSettingsContextType | undefined
>(undefined);

type SemanticLegendSettingsProps = Pick<
  SemanticLegendSettingsContextType,
  | 'clearSetting'
  | 'settingLevel'
  | 'settingOverrideCounts'
  | 'trackSemanticLegendSettings'
> & {
  runsetRef: RunSetRef;
  children: React.ReactNode;
};

export const useRunsFilter = (runsetRef: RunSetRef) => {
  const runSet = usePart(runsetRef);
  const filters = usePart(runSet.filtersRef);
  const groupSelections = usePart(runSet.groupSelectionsRef);
  const runsFilter = combinedFilter(
    filters ?? EMPTY_FILTERS,
    groupSelections,
    undefined,
    undefined,
    runSet.search
  );
  return runsFilter;
};

const useUpdateSemanticLegendRunColorOptions = (
  workspaceSettingsRef: WorkspaceSettingsRef
) => {
  // Update the semantic legend run color view action
  const updateSemanticLegendRunColorAction = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateSemanticLegendRunColorOptions
  );
  // Update the semantic legend run color options
  return useCallback(
    (value: RunColorOptions) => {
      updateSemanticLegendRunColorAction(value);
      const action =
        value === RunColorOptions.KeyBasedColors ? 'enabled' : 'disabled';
      logCustomActionToDatadog(`semanticLegend.${action}`);
    },
    [updateSemanticLegendRunColorAction]
  );
};

export const SemanticLegendSettingsContextProvider = ({
  children,
  clearSetting,
  runsetRef,
  settingLevel,
  settingOverrideCounts,
  trackSemanticLegendSettings,
}: SemanticLegendSettingsProps) => {
  const {workspaceSettingsRef, viewRef} = useWorkspaceRefsContext();
  const workspaceSettings = usePart(workspaceSettingsRef);
  const semanticLegendPalette = useWorkspaceSettingsSemanticLegendPalette();
  const view = useSelector(state => state.views.views[viewRef.viewID]);
  const canSaveWorkspace = useIsSaveWorkspaceAllowed(view);

  const semanticLegendRunColorOptions =
    workspaceSettings?.semanticLegendRunColorOptions ??
    RunColorOptions.OriginalProjectColors;

  const runSet = usePart(runsetRef);
  const filters = usePart(runSet.filtersRef);
  const groupSelections = usePart(runSet.groupSelectionsRef);
  const runsFilter = combinedFilter(
    filters ?? undefined,
    groupSelections,
    undefined,
    undefined,
    runSet.search
  );
  const runsSort = usePart(runSet.sortRef);
  const {projectInternalId} = useRunSetProject(runSet.project);
  const {hasActiveGrouping} = useGrouping(runsetRef);

  const configKeys = useWorkspaceConfigKeys(projectInternalId);

  const isKeyBasedColors =
    semanticLegendRunColorOptions === RunColorOptions.KeyBasedColors;

  // Update the semantic legend run color options
  const updateSemanticLegendRunColorOptions =
    useUpdateSemanticLegendRunColorOptions(workspaceSettingsRef);

  const [createSemanticLegendMutation] = useCreateSemanticLegendMutation({
    context: {
      propagateErrors: true,
    },
  });
  const [updateSemanticLegendMutation] = useUpdateSemanticLegendMutation();

  const updateSemanticLegendSettings = useCallback(
    (semanticLegendSettingsData: SemanticLegendSettings) => {
      // if the user is not allowed to save the workspace, we don't want to update the semantic legend settings
      if (!canSaveWorkspace) {
        return;
      }

      // if the semantic legend settings are the default settings or if the view id is not found, we don't want to update the semantic legend settings
      if (
        isDefaultSemanticLegendSettings(semanticLegendSettingsData) ||
        !view?.id
      ) {
        return;
      }

      // if the user wants to recalculate the buckets, we need to recalculate the buckets
      if (semanticLegendSettingsData.shouldRecalculateBuckets) {
        semanticLegendSettingsData.buckets = recalculateSemanticLegendBuckets(
          semanticLegendSettingsData?.numberOfBuckets ?? 0,
          semanticLegendSettingsData?.metricStatistics ?? {},
          semanticLegendSettingsData?.yAxisValue ??
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue,
          semanticLegendPalette
        );
        semanticLegendSettingsData.shouldRecalculateBuckets = false;
      }

      // update the semantic legend settings
      updateSemanticLegendMutation({
        variables: {
          id: view?.id,
          currentMetric: semanticLegendSettingsData.key ?? '',
          ...getMutationVarsFromSettings(semanticLegendSettingsData),
        },
        refetchQueries: [
          {
            query: SemanticLegendsQueryDocument,
            variables: {id: view?.id ?? ''},
          },
        ],
      });
    },
    [
      view?.id,
      updateSemanticLegendMutation,
      canSaveWorkspace,
      semanticLegendPalette,
    ]
  );

  const createSemanticLegendSettings = useCallback(
    async (semanticLegendSettingsData: SemanticLegendSettings) => {
      // if the view id is not found, we don't want to create the semantic legend settings
      if (!semanticLegendSettingsData.viewID) {
        console.error('No view ID found');
        return;
      }

      if (!canSaveWorkspace) {
        return;
      }
      try {
        // create the semantic legend settings
        await createSemanticLegendMutation({
          variables: {
            id: semanticLegendSettingsData.viewID,
            ...getMutationVarsFromSettings(semanticLegendSettingsData),
          },
          refetchQueries: [
            {
              query: SemanticLegendsQueryDocument,
              variables: {id: semanticLegendSettingsData.viewID ?? ''},
            },
          ],
        });
      } catch (error) {
        console.error('Error creating semantic legend settings', error);
      }
      return;
    },
    [createSemanticLegendMutation, canSaveWorkspace]
  );

  const {
    semanticLegendSettingsData: trueData,
    semanticLegendSettingsLoading,
    specPreviouslyCreated,
  } = useSemanticLegendSettingsQueryProps({
    entityName: view?.project?.entityName ?? '',
    projectName: view?.project?.name ?? '',
    viewID: view?.id ?? '',
    viewName: view?.name ?? '',
    filters: JSON.stringify(runsFilter),
    order: runsSort,
    updateSemanticLegendSettings: updateSemanticLegendSettings,
    createSemanticLegendSettings: createSemanticLegendSettings,
    configKeys: configKeys,
  });

  /**
   * Rules for updating semantic legends
   * 1. You can only create a new spec when it hasn't been created and you have a viewID on the new data
   * 2. You can only update a spec when it's previously been created and you have a viewID. The viewID could be from (a) the just now created spec or (b) the viewID on this page
   */
  function updateRules(
    newSettings: Partial<SemanticLegendSettings>,
    viewID: string,
    {
      alreadyCreated,
      isLoading,
    }: {
      alreadyCreated: boolean;
      isLoading: boolean;
    }
  ): [canCreate: boolean, canUpdate: boolean] {
    const canCreate = !isLoading && !alreadyCreated && !!newSettings.viewID;
    const canUpdate = alreadyCreated && !!viewID;
    return [canCreate, canUpdate];
  }

  const update = useCallback(
    async (newSettings: Partial<SemanticLegendSettings>) => {
      // if the user is not allowed to save the workspace, we don't want to create the semantic legend settings
      if (!canSaveWorkspace) {
        return;
      }

      const [canCreate, canUpdate] = updateRules(newSettings, view?.id ?? '', {
        alreadyCreated: specPreviouslyCreated,
        isLoading: semanticLegendSettingsLoading,
      });
      let mutableCanUpdate = canUpdate;

      if (canCreate) {
        await createSemanticLegendSettings({
          ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
          viewID: newSettings.viewID,
          ...getMutationVarsFromSettings(newSettings),
        });

        // once a spec is created we can update it
        mutableCanUpdate = true;
      }

      if (mutableCanUpdate) {
        updateSemanticLegendSettings(newSettings);
      }
    },
    [
      canSaveWorkspace,
      specPreviouslyCreated,
      createSemanticLegendSettings,
      updateSemanticLegendSettings,
      semanticLegendSettingsLoading,
      view?.id,
    ]
  );

  const {optimisticData, updateOptimistically} = useOptimisticUpdate({
    dataFromServer: trueData || DEFAULT_SEMANTIC_LEGEND_SETTINGS,
    debounceMs: 500,
    onError: error =>
      console.error('Error updating semantic legend settings', error),
    updateFn: update,
  });

  useGroupingToastNotification(
    hasActiveGrouping,
    semanticLegendRunColorOptions
  );

  const value = useMemo(
    () => ({
      type: 'without_categorical_config' as const,
      clearSetting,
      hasActiveGrouping,
      isMetricBasedColors: isKeyBasedColors,
      configKeys,
      semanticLegendRunColorOptions,
      semanticLegendSettings: optimisticData,
      settingLevel,
      settingOverrideCounts,
      specPreviouslyCreated,
      trackSemanticLegendSettings,
      updateSemanticLegendRunColorOptions,
      updateSemanticLegendSettings,
      updateSemanticLegendSettingsOptimistically: updateOptimistically,
    }),
    [
      clearSetting,
      hasActiveGrouping,
      isKeyBasedColors,
      optimisticData,
      configKeys,
      semanticLegendRunColorOptions,
      settingLevel,
      settingOverrideCounts,
      specPreviouslyCreated,
      trackSemanticLegendSettings,
      updateSemanticLegendRunColorOptions,
      updateSemanticLegendSettings,
      updateOptimistically,
    ]
  );

  return (
    <SemanticLegendSettingsContext.Provider value={value}>
      {children}
    </SemanticLegendSettingsContext.Provider>
  );
};

export const useSemanticLegendSettingsContext = ():
  | SemanticLegendSettingsContextType
  | SemanticLegendSettingsWithCategoricalConfigContextType => {
  const {entityName} = useParams<{entityName: string}>();
  const isCategoricalConfigEnabled =
    useRampFlagSemanticLegendsCategoricalConfig(entityName);

  const categoricalConfigValue = useContext(
    SemanticLegendSettingsWithCategoricalConfigContext
  );
  const regularValue = useContext(SemanticLegendSettingsContext);

  const value = isCategoricalConfigEnabled
    ? categoricalConfigValue
    : regularValue;

  if (value == null) {
    if (isCategoricalConfigEnabled) {
      return {
        type: 'with_categorical_config',
        clearSetting: () => {},
        hasActiveGrouping: false,
        configKeys: [],
        semanticLegendRunColorOptions: RunColorOptions.OriginalProjectColors,
        semanticLegendSettings: DEFAULT_SEMANTIC_LEGEND_SETTINGS,
        isMetricBasedColors: false,
        settingLevel: 'workspace',
        settingOverrideCounts: {},
        specPreviouslyCreated: false,
        trackSemanticLegendSettings: () => {},
        updateSemanticLegendRunColorOptions: () => {},
        updateSemanticLegendSettings: () => {},
        isLoadingBuckets: true,
      };
    }
    return {
      type: 'without_categorical_config',
      clearSetting: () => {},
      hasActiveGrouping: false,
      configKeys: [],
      semanticLegendRunColorOptions: RunColorOptions.OriginalProjectColors,
      semanticLegendSettings: DEFAULT_SEMANTIC_LEGEND_SETTINGS,
      isMetricBasedColors: false,
      settingLevel: 'workspace',
      settingOverrideCounts: {},
      specPreviouslyCreated: false,
      trackSemanticLegendSettings: () => {},
      updateSemanticLegendRunColorOptions: () => {},
      updateSemanticLegendSettings: () => {},
    };
  }

  return value;
};

export const useIsInSemanticLegendSettingsContext = (): boolean => {
  return useContext(SemanticLegendSettingsContext) !== undefined;
};
